'use client';

import { useEffect, useRef, useState } from 'react';

import { CasesText } from '../../components/CasesText';

import { ImageZoom } from '@/shared/components/ImageZoom';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface StickyScrollProps {
  items: {
    title: string;
    description: string;
    imgSrc: string;
    alt: string;
  }[];
  title: string;
}

export const StickyScroll = ({ items, title }: StickyScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrolledPixels, setScrolledPixels] = useState(0);

  const isMobile = useIsMobile();

  const cardHeight = isMobile ? 488 : 300;
  const gap = 24;
  const titleHeight = 100;

  const maxMovement = (items.length - 1) * (cardHeight + gap);

  const containerHeight = titleHeight + maxMovement + cardHeight + 50;

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();

      if (rect.top <= 0) {
        const scrolledFromStart = Math.abs(rect.top);

        if (scrolledFromStart <= maxMovement) {
          setScrolledPixels(scrolledFromStart);
        } else {
          setScrolledPixels(maxMovement);
        }
      } else {
        setScrolledPixels(0);
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [maxMovement]);

  const movement = Math.min(scrolledPixels, maxMovement);

  const cardsContainerHeight = cardHeight;

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ height: `${containerHeight}px` }}
    >
      <div className="sticky top-[50px] z-10 max-sm:top-[90px]">
        <CasesText size="medium" className="mb-9">
          {title}
        </CasesText>

        <div
          className="relative"
          style={{
            height: `${cardsContainerHeight}px`,
          }}
        >
          {items.map((item, index) => {
            const initialTop = index * (cardHeight + gap);

            const currentTop = Math.max(0, initialTop - movement);

            return (
              <Card
                key={item.title}
                image={item.imgSrc}
                title={item.title}
                description={item.description}
                style={{
                  position: 'absolute',
                  top: `${currentTop}px`,
                  left: 0,
                  right: 0,
                  zIndex: index + 1,
                }}
                alt={item.alt}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Card = ({
  image,
  title,
  description,
  style,
  alt,
}: {
  image: string;
  title: string;
  description: string;
  style?: React.CSSProperties;
  alt: string;
}) => {
  return (
    <div
      className="bg-surface-base-secondary flex h-[300px] w-full gap-9 rounded-[20px] p-2 shadow-[0px_-8px_16px_0px_#0000001F] max-sm:h-[488px] max-sm:flex-col max-sm:gap-5 max-sm:p-6"
      style={style}
    >
      <ImageZoom fullSrc={image} alt={alt}>
        <img
          src={image}
          alt={alt}
          className="w-[358px] max-w-none rounded-[12px] object-cover max-sm:max-h-[212px] max-sm:w-full"
        />
      </ImageZoom>
      <div className="flex flex-col justify-center gap-3">
        <CasesText size="medium">{title}</CasesText>

        <CasesText size="small" className="pr-8 max-sm:pr-0">
          {description}
        </CasesText>
      </div>
    </div>
  );
};
